import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, CheckCircle, Send, Trash2, Scan } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';
import { validateBundleFormat } from '@/utils/validation';

const Packing = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [usnCode, setUsnCode] = useState('');
  const [scannedUsns, setScannedUsns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const usnInputRef = useRef(null);

  // Auto-focus USN input when staff is locked
  useEffect(() => {
    if (staffIdLocked && usnInputRef.current) {
      usnInputRef.current.focus();
    }
  }, [staffIdLocked]);

  // Refocus USN input after each scan
  const focusUsnInput = () => {
    setTimeout(() => {
      if (usnInputRef.current) {
        usnInputRef.current.focus();
        usnInputRef.current.select();
      }
    }, 50);
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
    }
  };

  const handleUnlockStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
  };

  const handleUsnScan = async (e) => {
    e.preventDefault();
    
    const code = usnCode.trim();
    if (!code) {
      toast.error('Please scan USN code');
      setUsnCode('');
      focusUsnInput();
      return;
    }

    // Check if already scanned in current session
    if (scannedUsns.includes(code)) {
      toast.error('USN already scanned in this session');
      setUsnCode('');
      focusUsnInput();
      return;
    }

    setIsProcessing(true);
    try {
      // Validate USN
      const response = await apiClient.post(`/goods-transfer/packing/validate-usn?usn_code=${encodeURIComponent(code)}`);
      
      if (response.data.valid) {
        setScannedUsns(prev => [code, ...prev]);
        // Removed success toast per user request - only show on errors
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid USN';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
      setUsnCode('');
      focusUsnInput();
    }
  };

  const handleRemoveUsn = (code) => {
    setScannedUsns(prev => prev.filter(u => u !== code));
    toast.success('USN removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bundleBarcode.trim()) {
      toast.error('Please scan bundle barcode');
      bundleInputRef.current?.focus();
      return;
    }

    if (scannedUsns.length === 0) {
      toast.error('No USNs scanned');
      return;
    }

    // STRICT VALIDATION: defer to the shared `validateBundleFormat`
    // utility — accepts legacy Salem `BUN######/##-##` (15 chars) AND
    // site-prefixed `<SITE>-BUN######/##-##` for Srivilliputhur (SVPR-)
    // / Chennai (CHN-). 2026-05-20 — previously this branch hard-required
    // `startsWith('BUN')` + length 15, which rejected every Srivilliputhur
    // sticker (`SVPR-BUN…/26-27`) on the Honeywell scanner.
    const bundle = bundleBarcode.trim().toUpperCase();
    const formatCheck = validateBundleFormat(bundle);

    if (!formatCheck.valid) {
      const hint = bundle.includes('BUN')
        ? `${formatCheck.error}. Got: "${bundle}".`
        : `Must contain "BUN". You may have scanned a USN code instead of a Bundle code. Got: "${bundle}".`;
      toast.error(`❌ Invalid bundle barcode! ${hint}`);
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/goods-transfer/packing/submit', {
        staff_id: staffId,
        bundle_barcode: bundle,
        usn_codes: scannedUsns
      });
      
      toast.success(response.data.message);
      
      // Reset for next bundle
      setScannedUsns([]);
      setBundleBarcode('');
      focusUsnInput();
    } catch (error) {
      console.error('Submit error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : 'Failed to submit');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/goods-transfer')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 1: Packing</h1>
              <p className="text-sm text-[#9CA3AF]">Pack USNs into bundles for transfer</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Step 1: Staff ID */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <User className="w-5 h-5 text-orange-500" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                value={staffId}
                onChange={setStaffId}
                onValidated={handleStaffValidated}
                placeholder="Scan or enter Staff ID..."
                autoFocus
                showAutocomplete={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg">
                  <span className="font-mono font-semibold">{staffId}</span>
                  {staffName && <span className="ml-2 text-orange-600">- {staffName}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={handleUnlockStaff}>
                  Change
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Scan USNs (FIRST) */}
        {staffIdLocked && (
          <Card className="bg-white border-2 border-green-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <Scan className="w-5 h-5 text-green-500" />
                Scan USNs
              </CardTitle>
              <CardDescription>Scan all USN codes to pack into the bundle</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUsnScan} className="flex gap-2">
                <Input
                  ref={usnInputRef}
                  value={usnCode}
                  onChange={(e) => setUsnCode(e.target.value)}
                  placeholder="Scan USN code..."
                  className="text-lg h-12 font-mono"
                  disabled={isProcessing}
                  autoComplete="off"
                />
                <Button type="submit" disabled={isProcessing} className="bg-green-500 hover:bg-green-600 h-12 px-6">
                  {isProcessing ? 'Validating...' : 'Add'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scanned USNs List */}
        {scannedUsns.length > 0 && (
          <Card className="bg-white border-none shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Scanned USNs ({scannedUsns.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {scannedUsns.map((usn, index) => (
                  <div key={usn} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-8">#{scannedUsns.length - index}</span>
                      <span className="font-mono font-semibold">{usn}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveUsn(usn)}
                      className="text-red-500 hover:bg-red-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Bundle Barcode (LAST - after packing, paste sticker) */}
        {scannedUsns.length > 0 && (
          <Card className="bg-white border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Package className="w-5 h-5 text-blue-500" />
                Scan Bundle Barcode
              </CardTitle>
              <CardDescription>Pack the USNs and scan the bundle sticker barcode</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle Barcode..."
                  className="text-lg h-12 font-mono"
                  disabled={isSubmitting}
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting || !bundleBarcode.trim()}
                  className="bg-orange-500 hover:bg-orange-600 h-12 px-6"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Packing...' : 'Pack Bundle'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Summary info */}
        {staffIdLocked && scannedUsns.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Scan className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Start scanning USN codes to pack them into a bundle</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Packing;
